import * as React from "react";
import { useContext } from "react";
import { HashLink as Link } from "react-router-hash-link";
import { StatusErrorIcon, StatusOkIcon } from "affinityIcons";
import { differenceInYears } from "date-fns";

import { Summary } from "affinityUI/data/summary";
import { Address, DateTime, NhsNumber, PatientConsent, PreferredContact } from "affinityUI/data/valueDisplay";
import { VisuallyHidden } from "affinityUI/elements/visuallyHidden";
import { PreviousPatientData, PreviousPatientDataContext } from "components/pages/patients/editPatient";
import { SummaryBuilder } from "components/views/patientDetails/ui/summary";

import type { PatientFormData } from "../../types";

export const labels = {
  dob: "DOB",
  hospitalNumber: "Hospital number",
  nhsNumber: "NHS number",
  sex: "Sex",
  oncologist: "Oncologist",
  primaryContact: "Primary contact",
  secondaryContact: "Secondary contact",
  email: "Email",
  address: "Address",
  preferredContactMethod: "Contact preference",
  consent: {
    service: "Service",
    privacyPolicy: "Privacy policy",
    feedback: "Feedback",
    research: "Research",
  },
  yes: (
    <>
      <StatusOkIcon /> Yes
    </>
  ),
  no: (
    <>
      <StatusErrorIcon /> No
    </>
  ),
};

const createSections = ({
  patient,
  previousPatientData,
}: {
  patient: PatientFormData;
  previousPatientData: PreviousPatientData;
}) => {
  const { firstName, lastName, hospitalNumber, dateOfBirth, nhsNumber, sex, oncologist, contactDetails, consent } =
    patient;
  const age = differenceInYears(new Date(), new Date(dateOfBirth));
  return [
    {
      id: "info",
      title: "Patient information",
      fields: [
        {
          label: "First name",
          description: firstName,
        },
        {
          label: "Last name",
          description: lastName,
        },
        {
          label: labels.dob,
          description: (
            <>
              <DateTime format={DateTime.FORMAT.DATE} value={dateOfBirth} /> ({age})
            </>
          ),
        },
        {
          label: labels.hospitalNumber,
          description: hospitalNumber,
        },
        {
          label: labels.nhsNumber,
          description: <NhsNumber value={nhsNumber} />,
        },
        {
          label: labels.sex,
          description: sex,
        },
      ],
    },
    {
      id: "address",
      title: "Address",
      fields: [
        {
          label: labels.address,
          description: <Address value={contactDetails.address} />,
        },
      ],
    },
    {
      id: "contact",
      title: "Contact information",
      fields: [
        {
          label: labels.oncologist,
          description: oncologist,
        },
        {
          label: labels.primaryContact,
          description: <address>{contactDetails.primaryContact}</address>,
        },
        {
          label: labels.secondaryContact,
          description: <address>{contactDetails.secondaryContact}</address>,
        },
        {
          label: labels.email,
          description: <address>{contactDetails.email}</address>,
        },
        {
          label: labels.preferredContactMethod,
          description: <PreferredContact value={contactDetails.preferredContactMethod} />,
        },
      ],
    },
    {
      id: "consent",
      title: "Patient consents",
      fields: [
        {
          label: labels.consent.service,
          description: (
            <PatientConsent
              consentType="service"
              value={consent.service}
              lastUpdated={{
                ...previousPatientData?.consent?.serviceLastUpdate?.[0],
                updatingNow: previousPatientData?.consent?.service !== consent.service,
              }}
            />
          ),
        },
        {
          label: labels.consent.privacyPolicy,
          description: (
            <PatientConsent
              consentType="privacyPolicy"
              value={consent.privacyPolicy}
              lastUpdated={{
                ...previousPatientData?.consent?.privacyPolicyLastUpdate?.[0],
                updatingNow: previousPatientData?.consent?.privacyPolicy !== consent.privacyPolicy,
              }}
            />
          ),
        },
        {
          label: labels.consent.feedback,
          description: (
            <PatientConsent
              consentType="feedback"
              value={consent.feedback}
              lastUpdated={{
                ...previousPatientData?.consent?.feedbackLastUpdate?.[0],
                updatingNow: previousPatientData?.consent?.feedback !== consent.feedback,
              }}
            />
          ),
        },
        {
          label: labels.consent.research,
          description: (
            <PatientConsent
              consentType="research"
              value={consent.research}
              lastUpdated={{
                ...previousPatientData?.consent?.researchLastUpdate?.[0],
                updatingNow: previousPatientData?.consent?.research !== consent.research,
              }}
            />
          ),
        },
      ],
    },
  ];
};

const PatientSummary: React.FC<{ patient: PatientFormData; formLink: string }> = ({ patient, formLink }) => {
  const previousPatientData = useContext(PreviousPatientDataContext);
  const sectionsData = createSections({ patient, previousPatientData });
  const sections = sectionsData.map(({ id, title, fields }) => (
    <Summary.Section
      key={id}
      title={title}
      actions={
        <Link to={`${formLink}#${id}`}>
          Edit <VisuallyHidden>{title}</VisuallyHidden>
        </Link>
      }
    >
      <SummaryBuilder fields={fields} />
    </Summary.Section>
  ));
  return <>{sections}</>;
};

export default PatientSummary;
